import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BarChart, ChartsReferenceLine, LineChart } from "@mui/x-charts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiQuery } from "../../../hooks/use-api-query";
import { CHART_COLORS, STATUS_COLORS, theme } from "../../../theme/theme";
import type { TimeGranularity, TimeSeriesFilters, TimeSeriesResponse } from "../../../types";
import type { DateRangeValue } from "./date-range-filter";

// Color palette for grouped series
const GROUP_COLORS = [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.success.main,
  theme.palette.warning.main,
  theme.palette.info.main,
  theme.palette.error.main,
  STATUS_COLORS.purple.border, // purple
  CHART_COLORS[4], // cyan/blue
  CHART_COLORS[3], // orange
  CHART_COLORS[7], // light green/mixed
];

interface TimeSeriesChartProps {
  title: string;
  subtitle: string;
  endpoint: string;
  chartType: "bar" | "line";
  defaultGranularity: TimeGranularity;
  filters: TimeSeriesFilters;
  dateRange: DateRangeValue;
  valueFormatter?: (value: number | null) => string;
  color?: string;
  icon?: React.ReactNode;
  referenceLine?: {
    y: number;
    label: string;
    tooltip?: string;
    color?: string;
  };
  /** External refetch trigger - increment to trigger refetch */
  refetchTrigger?: number;
}

function buildQueryString(
  dateRange: DateRangeValue,
  granularity: TimeGranularity,
  filters: TimeSeriesFilters,
): string {
  const params = new URLSearchParams();

  // Date range
  if (dateRange.startDate && dateRange.endDate) {
    params.set("start_date", dateRange.startDate);
    params.set("end_date", dateRange.endDate);
  } else if (dateRange.period) {
    params.set("period", dateRange.period);
  }

  // Granularity
  params.set("granularity", granularity);

  // Filters
  if (filters.testSuiteIds?.length) {
    for (const id of filters.testSuiteIds) {
      params.append("test_suite_ids", id);
    }
  }
  if (filters.appConfigIds?.length) {
    for (const id of filters.appConfigIds) {
      params.append("app_config_ids", id);
    }
  }
  if (filters.groupByAppConfig) {
    params.set("group_by_app_config", "true");
  }

  return params.toString();
}

function formatPeriodLabel(periodStart: string, granularity: TimeGranularity) {
  const date = new Date(periodStart);

  switch (granularity) {
    case "day":
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    case "week":
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    case "month":
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
      });
    default:
      return date.toLocaleDateString();
  }
}

export function TimeSeriesChart({
  title,
  subtitle,
  endpoint,
  chartType,
  defaultGranularity,
  filters,
  dateRange,
  valueFormatter = (v) => (v != null ? v.toLocaleString() : "-"),
  color = theme.palette.primary.main,
  icon,
  referenceLine,
  refetchTrigger,
}: TimeSeriesChartProps) {
  const [granularity, setGranularity] = useState<TimeGranularity>(defaultGranularity);

  const queryString = useMemo(
    () => buildQueryString(dateRange, granularity, filters),
    [dateRange, granularity, filters],
  );

  const { data, isLoading, isFetching, refetch } = useApiQuery<TimeSeriesResponse>(
    `${endpoint}?${queryString}`,
    {
      // Disable automatic refetches to reduce DB load
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    },
  );

  // Refetch when external trigger changes
  useEffect(() => {
    if (refetchTrigger && refetchTrigger > 0) {
      refetch();
    }
  }, [refetchTrigger, refetch]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Process data for charting
  const chartData = useMemo(() => {
    if (!data?.data?.length) return null;

    const isGrouped = filters.groupByAppConfig && data.groups?.length > 0;

    if (isGrouped) {
      // Group data by period, then by group
      const periodMap = new Map<string, { label: string; values: Map<string, number> }>();

      data.data.forEach((point) => {
        const label = formatPeriodLabel(point.period_start, granularity);
        if (!periodMap.has(point.period_start)) {
          periodMap.set(point.period_start, { label, values: new Map() });
        }
        const groupId = point.group_id || "unknown";
        periodMap.get(point.period_start)?.values.set(groupId, point.value);
      });

      const periods = Array.from(periodMap.entries()).sort(
        ([a], [b]) => new Date(a).getTime() - new Date(b).getTime(),
      );

      const labels = periods.map(([, { label }]) => label);
      const series = data.groups.map((group, idx) => ({
        data: periods.map(([, { values }]) => values.get(group.id) ?? (null as number | null)),
        label: group.name,
        color: GROUP_COLORS[idx % GROUP_COLORS.length],
        curve: "linear" as const,
      }));

      return { labels, series };
    }

    // Simple non-grouped data
    // Using toSorted() for immutability (React best practice: preserves original array)
    const sortedData = data.data.toSorted(
      (a, b) => new Date(a.period_start).getTime() - new Date(b.period_start).getTime(),
    );

    const labels = sortedData.map((point) => formatPeriodLabel(point.period_start, granularity));
    const values = sortedData.map((point) => point.value);

    return {
      labels,
      series: [{ data: values, color, curve: "linear" as const }],
    };
  }, [data, granularity, filters.groupByAppConfig, color]);

  const isEmpty = !chartData || chartData.labels.length === 0;

  // Y-axis formatter (strip null handling for axis labels)
  const yAxisValueFormatter = (v: number) => valueFormatter(v).replace("-", "0");

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%", pb: 1 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {icon}
              <Typography variant="h6">{title}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          {/* Controls */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  animation: isFetching ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <ButtonGroup size="small" variant="outlined">
              <Button
                onClick={() => setGranularity("day")}
                variant={granularity === "day" ? "contained" : "outlined"}
              >
                Day
              </Button>
              <Button
                onClick={() => setGranularity("week")}
                variant={granularity === "week" ? "contained" : "outlined"}
              >
                Week
              </Button>
              <Button
                onClick={() => setGranularity("month")}
                variant={granularity === "month" ? "contained" : "outlined"}
              >
                Month
              </Button>
            </ButtonGroup>
          </Stack>
        </Stack>

        {/* Chart - pinned to bottom */}
        <Box sx={{ mt: "auto" }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={280} />
          ) : isEmpty ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 280,
              }}
            >
              <Typography color="text.secondary">
                No data available for the selected filters
              </Typography>
            </Box>
          ) : chartType === "bar" ? (
            <BarChart
              series={chartData.series.map((s) => ({
                ...s,
                valueFormatter,
              }))}
              xAxis={[
                {
                  data: chartData.labels,
                  scaleType: "band",
                },
              ]}
              yAxis={[
                {
                  valueFormatter: yAxisValueFormatter,
                },
              ]}
              height={280}
              hideLegend={chartData.series.length <= 1}
            >
              {referenceLine && (
                <Tooltip title={referenceLine.tooltip || ""} placement="right">
                  <ChartsReferenceLine
                    y={referenceLine.y}
                    label={referenceLine.label}
                    labelAlign="end"
                    lineStyle={{
                      stroke: referenceLine.color || theme.palette.error.main,
                      strokeWidth: 1.5,
                      strokeDasharray: "3 3",
                    }}
                    labelStyle={{
                      fill: referenceLine.color || theme.palette.error.main,
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                    spacing={{
                      x: -20,
                      y: -2,
                    }}
                  />
                </Tooltip>
              )}
            </BarChart>
          ) : (
            <LineChart
              series={chartData.series.map((s) => ({
                ...s,
                valueFormatter,
              }))}
              xAxis={[
                {
                  data: chartData.labels,
                  scaleType: "band",
                },
              ]}
              yAxis={[
                {
                  valueFormatter: yAxisValueFormatter,
                },
              ]}
              height={280}
              hideLegend={chartData.series.length <= 1}
            >
              {referenceLine && (
                <ChartsReferenceLine
                  y={referenceLine.y}
                  label={referenceLine.label}
                  labelAlign="end"
                  lineStyle={{
                    stroke: referenceLine.color || theme.palette.error.main,
                    strokeWidth: 2.5,
                  }}
                  labelStyle={{
                    fill: referenceLine.color || theme.palette.error.main,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                />
              )}
            </LineChart>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
